import React, { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { capDisplayFreshness } from '../../utils/freshnessDisplay';
import { getCurrentLanguage } from '../../services/i18n';
import { findProductMetadata } from '../../utils/groceryUtils';
import ImagePreviewDrawer from '../ProductList/admin/ImagePreviewDrawer';
import {
  getProductImage,
  getLocalizedProductName,
  findMatchedProduct,
} from '../../utils/recipeIngredientMatching';

export const RecipeInstructions = ({ instructions }) => {
  const { t } = useTranslation('common');
  if (typeof instructions === 'string') {
    try {
      instructions = JSON.parse(instructions);
    } catch (e) {
      return <div className="text-[#65635D] text-[14px] leading-[24px] whitespace-pre-wrap">{instructions}</div>;
    }
  }

  if (instructions && typeof instructions === 'object') {
    const cookingSteps = instructions.cooking_instructions || [];

    return (
      <div className="space-y-8">
        {cookingSteps.length > 0 ? (
          cookingSteps.map((step, index) => {
            let parsedStep = step;

            if (typeof step === 'string' && step.trim().startsWith('{')) {
              try {
                parsedStep = JSON.parse(step);
              } catch (e) {
                parsedStep = { title: step };
              }
            }

            const stepTitle = parsedStep.step_title || parsedStep.title || `Step ${index + 1}`;
            const stepDetails = parsedStep.details || [];
            const hasSubsteps = Array.isArray(stepDetails) && stepDetails.length > 0;

            return (
              <div key={index} className="relative">
                <div className="flex items-start mb-4">
                  <div className="relative flex-shrink-0 mr-2 w-8 h-8">
                    <div className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center relative z-10 shadow-[0px_0px_0px_4px_#F8F8F7]">
                      <span className="text-[#0D0C07] text-[14px] font-medium leading-none">{index + 1}</span>
                    </div>
                  </div>

                  <div className="flex-1 pt-1">
                    <h4 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] font-bold leading-[24px]">
                      {typeof stepTitle === 'string' ? stepTitle : JSON.stringify(stepTitle)}
                    </h4>
                  </div>
                </div>

                {hasSubsteps && (
                  <div className="space-y-3">
                    {stepDetails.map((detail, detailIdx) => (
                      <div key={detailIdx} className="flex items-start">
                        <div className="flex-shrink-0 mr-4 relative">
                          <div className="w-4 h-4 bg-[#E2E1DF] ml-2 mt-2 shadow-[0px_0px_0px_4px_#F8F8F7] rounded-full flex items-center justify-center relative z-10">
                            <div className="w-[4px] h-[4px] bg-white rounded-full" />
                          </div>
                        </div>

                        <div className="flex-1 pt-[2px]">
                          <p className="text-[var(--Color-Grays-Gray-950,#353227)] text-[14.4px] leading-[26px]">
                            {typeof detail === 'string' ? detail : detail.detail ? detail.detail : JSON.stringify(detail)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!hasSubsteps && typeof parsedStep === 'string' && (
                  <div className="flex items-start -mt-4">
                    <div className="w-8 mr-4" />
                    <div className="flex-1">
                      <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[22px]">{parsedStep}</p>
                    </div>
                  </div>
                )}

                {hasSubsteps && (
                  <div
                    className="absolute left-[16px] bg-[#DCDCD8] transform -translate-x-1/2 w-[1px]"
                    style={{ top: '32px', bottom: '0px' }}
                  />
                )}
              </div>
            );
          })
        ) : (
          <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px]">{t('recipe.noInstructions', 'Cooking steps are not available yet.')}</p>
        )}
      </div>
    );
  }

  return <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px]">{t('recipe.instructionsNotAvailable', 'Cooking steps are not available.')}</p>;
};

const UNIT_TRANSLATIONS = {
  es: { 'to taste': 'al gusto', units: 'uds', unit: 'ud', tbsp: 'cda', tsp: 'cdta', cups: 'tazas', cup: 'taza', cloves: 'dientes', clove: 'diente', slices: 'rebanadas', slice: 'rebanada', pieces: 'uds', piece: 'ud', bunches: 'manojos', bunch: 'manojo', pinch: 'pizca', handful: 'puñado', sprigs: 'ramitas', sprig: 'ramita', leaves: 'hojas', leaf: 'hoja', heads: 'cabezas', head: 'cabeza', cans: 'latas', can: 'lata', large: 'grande', medium: 'mediano', small: 'pequeño' },
  ca: { 'to taste': 'al gust', units: 'uds', unit: 'ud', tbsp: 'cda', tsp: 'cdta', cups: 'tasses', cup: 'tassa', cloves: 'grans', clove: 'gra', slices: 'tallades', slice: 'tallada', pieces: 'uds', piece: 'ud', bunches: 'manats', bunch: 'manat', pinch: 'pessic', handful: 'grapat', sprigs: 'branquetes', sprig: 'branqueta', leaves: 'fulles', leaf: 'fulla', heads: 'caps', head: 'cap', cans: 'llaunes', can: 'llauna', large: 'gran', medium: 'mitjà', small: 'petit' },
  fr: { 'to taste': 'au goût', units: 'pcs', unit: 'pc', tbsp: 'c.à.s.', tsp: 'c.à.c.', cups: 'tasses', cup: 'tasse', cloves: 'gousses', clove: 'gousse', slices: 'tranches', slice: 'tranche', pieces: 'pcs', piece: 'pc', bunches: 'bottes', bunch: 'botte', pinch: 'pincée', handful: 'poignée', sprigs: 'brins', sprig: 'brin', leaves: 'feuilles', leaf: 'feuille', heads: 'têtes', head: 'tête', cans: 'boîtes', can: 'boîte', large: 'grand', medium: 'moyen', small: 'petit' },
  it: { 'to taste': 'q.b.', units: 'pz', unit: 'pz', tbsp: 'cucchiai', tsp: 'cucchiaini', cups: 'tazze', cup: 'tazza', cloves: 'spicchi', clove: 'spicchio', slices: 'fette', slice: 'fetta', pieces: 'pz', piece: 'pz', bunches: 'mazzi', bunch: 'mazzo', pinch: 'pizzico', handful: 'manciata', sprigs: 'rametti', sprig: 'rametto', leaves: 'foglie', leaf: 'foglia', heads: 'teste', head: 'testa', cans: 'lattine', can: 'lattina', large: 'grande', medium: 'medio', small: 'piccolo' },
  ro: { 'to taste': 'după gust', units: 'buc', unit: 'buc', tbsp: 'linguri', tsp: 'lingurițe', cups: 'căni', cup: 'cană', cloves: 'căței', clove: 'cățel', slices: 'felii', slice: 'felie', pieces: 'buc', piece: 'buc', bunches: 'legături', bunch: 'legătură', pinch: 'vârf', handful: 'pumn', sprigs: 'ramuri', sprig: 'ramură', leaves: 'frunze', leaf: 'frunză', heads: 'căpățâni', head: 'căpățână', cans: 'conserve', can: 'conservă', large: 'mare', medium: 'mediu', small: 'mic' },
  ru: { 'to taste': 'по вкусу', units: 'шт', unit: 'шт', tbsp: 'ст.л.', tsp: 'ч.л.', cups: 'стакана', cup: 'стакан', cloves: 'зубч.', clove: 'зубч.', slices: 'ломт.', slice: 'ломт.', pieces: 'шт', piece: 'шт', bunches: 'пучков', bunch: 'пучок', pinch: 'щепотка', handful: 'горсть', sprigs: 'веточек', sprig: 'веточка', leaves: 'листьев', leaf: 'лист', heads: 'головок', head: 'головка', cans: 'банок', can: 'банка', large: 'крупн.', medium: 'средн.', small: 'мелк.' },
  uk: { 'to taste': 'за смаком', units: 'шт', unit: 'шт', tbsp: 'ст.л.', tsp: 'ч.л.', cups: 'стакани', cup: 'стакан', cloves: 'зубч.', clove: 'зубч.', slices: 'скиб.', slice: 'скиб.', pieces: 'шт', piece: 'шт', bunches: 'пучків', bunch: 'пучок', pinch: 'дрібка', handful: 'жменя', sprigs: 'гілочок', sprig: 'гілочка', leaves: 'листків', leaf: 'листок', heads: 'головок', head: 'головка', cans: 'банок', can: 'банка', large: 'велик.', medium: 'середн.', small: 'мал.' },
};

export const translateQty = (qty) => {
  if (!qty) return qty;
  const lang = getCurrentLanguage();
  if (lang === 'en' || !UNIT_TRANSLATIONS[lang]) return qty;
  const map = UNIT_TRANSLATIONS[lang];
  let result = qty;
  for (const [en, loc] of Object.entries(map).sort((a, b) => b[0].length - a[0].length)) {
    result = result.replace(new RegExp(`\\b${en}\\b`, 'gi'), loc);
  }
  return result;
};

const getIngredientImage = (ingredient) => {
  if (ingredient?.imageURL) return ingredient.imageURL;

  const candidateNames = [
    ingredient?.product,
    ingredient?.name,
    ingredient?.originalName,
  ].filter(Boolean);

  for (const candidate of candidateNames) {
    const metadata = findProductMetadata(candidate);
    if (metadata?.image) {
      return `/assets/products/${metadata.image}`;
    }
  }

  return '/assets/products/no-image.jpg';
};

export default function RecipeContent({ recipe, promoMeal, isInline = false, title = null, tagline = null, showSectionTabs = false, productList = null, hideDescription = false, stickyFooterNav = false }) {
  const { t } = useTranslation('mealplan');
  const { t: tRecipes } = useTranslation('recipes');
  const [activeSectionTab, setActiveSectionTab] = useState('ingredients');
  const [previewImage, setPreviewImage] = useState(null);
  const instructionsRef = useRef(null);
  const ingredientsRef = useRef(null);
  const equipmentRef = useRef(null);

  // Build ingredient → matched product map (same as RecipeDrawer)
  const ingredientProducts = useMemo(() => {
    if (!recipe?.ingredients || !productList?.length) return {};
    const map = {};
    recipe.ingredients.forEach((ing) => {
      const key = ing.product || ing.name;
      if (key) map[key] = findMatchedProduct(ing, productList);
    });
    return map;
  }, [recipe?.ingredients, productList]);

  if (!recipe) return null;

  const hasIngredients = recipe.ingredients?.length > 0;

  const freshDays = promoMeal?.maxStorageTime
    ? capDisplayFreshness(parseInt(promoMeal.maxStorageTime, 10) || 0)
    : null;

  const mealTypeKey = promoMeal?.mealType
    ? String(promoMeal.mealType).toLowerCase()
    : null;
  const localizedMealType = mealTypeKey
    ? t(`mealTypes.${mealTypeKey}`, { defaultValue: promoMeal.mealType })
    : null;
  const cookingTimeMinutes = promoMeal?.cookingTime
    ? String(promoMeal.cookingTime).replace(/\s*min\s*$/i, '').trim()
    : null;

  const titleClass = isInline
    ? 'inline text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[18.8px] font-bold leading-[36px]'
    : 'inline text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[18.8px] font-bold leading-[36px] border-b-[2px] border-[#009900]';

  const sectionWrapClass = 'mt-8';

  const sectionHeaderClass = 'flex items-center justify-between mb-5 pt-8 border-t-[1px] border-[var(--Color-Grays-Gray-3,#DCDCD8)]';
  const showTopTabs = showSectionTabs && !stickyFooterNav;
  const firstSectionHeaderClass = (showSectionTabs || stickyFooterNav)
    ? 'flex items-center justify-between mb-5 pt-8'
    : sectionHeaderClass;

  const scrollToSection = (ref) => {
    if (!ref.current) return;
    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  };

  const handleSectionTabClick = (tab, ref) => {
    setActiveSectionTab(tab);
    scrollToSection(ref);
  };

  const sectionTabClass = (tab) => `flex-1 min-w-0 py-3 text-center justify-center flex items-center border-b-[3px] cursor-pointer transition-colors ${
    activeSectionTab === tab
      ? 'border-[#FFCB00] text-[var(--Color-Grays-Gray-10,#0D0C07)] font-bold'
      : 'text-[var(--Color-Grays-Gray-8,#4A4844)] font-medium border-transparent md:hover:text-[var(--Color-Grays-Gray-10,#0D0C07)] md:hover:border-[var(--Color-Grays-Gray-3,#DCDCD8)]'
  }`;

  const sectionTabStroke = (tab) => activeSectionTab === tab ? '#0D0C07' : '#65635D';
  const stickySectionButtonClass = (tab) => `relative flex-1 flex flex-col items-center gap-0 justify-center h-[48px] pt-[4px] rounded-[32px] text-[10.4px] leading-[16px] transition-colors md:hover:bg-[rgba(255,255,255,0.4)] ${
    activeSectionTab === tab
      ? 'bg-[rgba(255,255,255,0.6)] text-[var(--Color-Grays-Gray-10,#0D0C07)] font-semibold'
      : 'text-[var(--Color-Grays-Gray-7,#65635D)] font-normal'
  }`;

  return (
    <div>
      <div className="flex flex-row flex-wrap gap-1 mb-0.5">
        {localizedMealType && (
          <>
            <span className="text-[12.4px] font-normal text-[#65635D] leading-[20px]">{localizedMealType}</span>
            <span className="text-[12.4px] font-normal text-[#C1C0BE] leading-[20px]">•</span>
          </>
        )}
        {cookingTimeMinutes && (
          <>
            <span className="text-[12.4px] font-normal text-[#65635D] leading-[20px]">
              {t('recipeCard.min', { time: cookingTimeMinutes })}
            </span>
            <span className="text-[12.4px] font-normal text-[#C1C0BE] leading-[20px]">•</span>
          </>
        )}
        {freshDays > 0 && (
          <span className="text-[12.4px] font-normal text-[#65635D] leading-[20px]">
            {t('recipeCard.freshFor', { days: freshDays })}
          </span>
        )}
      </div>

      {title && (
        <h1 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] font-bold leading-[28px] w-full mb-2">
          {title}
        </h1>
      )}

      {tagline && (
        <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px] mb-3">
          {tagline}
        </p>
      )}

      {!hideDescription && recipe.instructions?.full_description && (
        <div className="mb-0 mt-2">
          <p className="text-[var(--Color-Grays-Gray-950,#353227)] text-[14px] leading-[24px] mb-0">{recipe.instructions.full_description}</p>
        </div>
      )}

      {showTopTabs && (
        <div className="mt-8 flex border-b border-[var(--Color-Grays-Gray-3,#E8E6E3)]">
          <button
            type="button"
            onClick={() => handleSectionTabClick('ingredients', ingredientsRef)}
            className={sectionTabClass('ingredients')}
          >
            <span className="flex-shrink-0 flex items-center justify-center w-[16px] h-[16px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 25 24" fill="none">
                <path d="M15.667 11L14.667 20M19.667 11L15.667 4M2.66699 11H22.667M4.16699 11L5.76699 18.4C5.86049 18.8586 6.11184 19.2698 6.47728 19.5621C6.84271 19.8545 7.2991 20.0094 7.76699 20H17.567C18.0349 20.0094 18.4913 19.8545 18.8567 19.5621C19.2221 19.2698 19.4735 18.8586 19.567 18.4L21.267 11M5.16699 15.5H20.167M5.66699 11L9.66699 4M9.66699 11L10.667 20" stroke={sectionTabStroke('ingredients')} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="ml-1.5 min-w-0 truncate text-[13.8px] sm:text-[15.8px] leading-[24px] sm:leading-[28px] h-7 py-0">
              {tRecipes('drawer.ingredients')}
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleSectionTabClick('instructions', instructionsRef)}
            className={sectionTabClass('instructions')}
          >
            <span className="flex-shrink-0 flex items-center justify-center w-[16px] h-[16px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 25 24" fill="none">
                <path d="M7.3335 21H17.3335M19.8335 12L22.3335 6M16.5835 3C16.8535 3.1 17.3835 3.53 17.3335 4.36C17.2735 5.19 16.4035 5.56 16.3335 6.38C16.2835 7.16 16.6735 7.62 17.0635 8M11.5835 3C11.8535 3.1 12.3835 3.53 12.3235 4.36C12.2735 5.19 11.3935 5.56 11.3435 6.38C11.2835 7.16 11.6735 7.62 12.0635 8M6.5835 3C6.8535 3.1 7.3835 3.53 7.3335 4.36C7.2735 5.19 6.4035 5.56 6.3335 6.38C6.2835 7.16 6.6735 7.62 7.0735 8M12.3335 21C14.7204 21 17.0096 20.0518 18.6975 18.364C20.3853 16.6761 21.3335 14.3869 21.3335 12H3.3335C3.3335 14.3869 4.28171 16.6761 5.96953 18.364C7.65736 20.0518 9.94655 21 12.3335 21Z" stroke={sectionTabStroke('instructions')} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="ml-1.5 min-w-0 truncate text-[13.8px] sm:text-[15.8px] leading-[24px] sm:leading-[28px] h-7 py-0">
              {tRecipes('drawer.instructions')}
            </span>
          </button>
        </div>
      )}

      {hasIngredients && (
        <div className={sectionWrapClass} ref={ingredientsRef}>
          <div className={firstSectionHeaderClass}>
            <h3 className={titleClass}>{tRecipes('drawer.ingredients')}</h3>
          </div>
          <div className="space-y-0">
            {recipe.ingredients.map((ingredient, idx) => {
              const fallbackName = ingredient.product || ingredient.name || '';
              const qty = translateQty(ingredient.quantity || ingredient.qty || '');
              const matchedProduct = ingredientProducts[ingredient.product || ingredient.name];
              // Always prefer Typesense product image (same source as RecipeDrawer).
              // If no match yet (live Typesense fetch still in flight), fall back to
              // the static PRODUCT_METADATA lookup so we don't flash a broken icon.
              const image = matchedProduct
                ? getProductImage(matchedProduct)
                : getIngredientImage(ingredient);
              const displayName =
                (matchedProduct ? getLocalizedProductName(matchedProduct) : null) ||
                fallbackName;
              const openPreview = () => setPreviewImage({ src: image, name: displayName });

              return (
                <div key={idx} className="flex items-center gap-3 py-3 border-b border-[var(--Color-Grays-Gray-2,#F0EFED)] last:border-b-0">
                  <button
                    type="button"
                    onClick={openPreview}
                    className="flex-shrink-0 w-12 h-12 rounded-[8px] overflow-hidden bg-gray-100 cursor-pointer active:opacity-70 transition-opacity"
                    aria-label={displayName}
                  >
                    <img
                      src={image}
                      alt={displayName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/assets/products/no-image.jpg';
                      }}
                    />
                  </button>

                  <div className="flex-1 min-w-0">
                    <h4 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold leading-[20px] truncate">{displayName}</h4>
                  </div>

                  {qty && (
                    <div className="flex-shrink-0">
                      <span className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[13px] font-medium leading-[20px] bg-[var(--Color-Grays-Gray-2,#F5F3F0)] px-2.5 py-1 rounded-[6px]">
                        {qty}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={sectionWrapClass} ref={instructionsRef}>
        <div className={hasIngredients ? sectionHeaderClass : firstSectionHeaderClass}>
          <h3 className={titleClass}>{t('recipeCard.cookingInstructions')}</h3>
        </div>
        {recipe.instructions ? (
          <RecipeInstructions instructions={recipe.instructions} />
        ) : (
          <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px]">{t('loading.loadingInstructions')}</p>
        )}
      </div>

      {recipe.instructions?.tips && (
        <div className={sectionWrapClass}>
          <div className={sectionHeaderClass}>
            <h3 className={titleClass}>{tRecipes('drawer.tips')}</h3>
          </div>
          <p className="block mt-5 text-[var(--Color-Grays-Gray-950,#353227)] text-[14.8px] leading-[26px]">{recipe.instructions.tips}</p>
        </div>
      )}

      {recipe.instructions?.equipment && Array.isArray(recipe.instructions.equipment) && recipe.instructions.equipment.length > 0 && (
        <div className={sectionWrapClass} ref={equipmentRef}>
          <div className={sectionHeaderClass}>
            <h3 className={titleClass}>{tRecipes('drawer.equipment')}</h3>
          </div>
          <ul className="space-y-2">
            {recipe.instructions.equipment.map((item, idx) => (
              <li key={idx} className="text-[var(--Color-Grays-Gray-950,#353227)] text-[14.8px] leading-[26px] flex items-start">
                <span className="mr-3 text-[var(--Color-Grays-Gray-10,#0D0C07)]">•</span>
                <span>{typeof item === 'string' ? item : item.equipment ? item.equipment : JSON.stringify(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ImagePreviewDrawer
        isOpen={!!previewImage}
        image={previewImage}
        closeDrawer={() => setPreviewImage(null)}
      />

      {stickyFooterNav && (
        <div
          className="fixed bottom-[16px] left-[20px] right-[20px] py-1 px-1 flex justify-center z-50 mx-auto max-w-[390px] rounded-[96px] bg-[linear-gradient(135deg,_rgba(243,243,242,0.8)_0%,_rgba(243,243,242,0.9)_100%)] backdrop-blur-[64px] border-[1px] border-[#fff] shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)] transition-[bottom] duration-500 ease-in-out"
        >
          <div className="flex justify-between items-center gap-1 w-full">
          <button
            type="button"
            onClick={() => handleSectionTabClick('ingredients', ingredientsRef)}
            className={stickySectionButtonClass('ingredients')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 24" fill="none">
              <path d="M15.667 11L14.667 20M19.667 11L15.667 4M2.66699 11H22.667M4.16699 11L5.76699 18.4C5.86049 18.8586 6.11184 19.2698 6.47728 19.5621C6.84271 19.8545 7.2991 20.0094 7.76699 20H17.567C18.0349 20.0094 18.4913 19.8545 18.8567 19.5621C19.2221 19.2698 19.4735 18.8586 19.567 18.4L21.267 11M5.16699 15.5H20.167M5.66699 11L9.66699 4M9.66699 11L10.667 20" stroke={sectionTabStroke('ingredients')} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{tRecipes('drawer.ingredients')}</span>
          </button>
          <button
            type="button"
            onClick={() => handleSectionTabClick('instructions', instructionsRef)}
            className={stickySectionButtonClass('instructions')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 24" fill="none">
              <path d="M7.3335 21H17.3335M19.8335 12L22.3335 6M16.5835 3C16.8535 3.1 17.3835 3.53 17.3335 4.36C17.2735 5.19 16.4035 5.56 16.3335 6.38C16.2835 7.16 16.6735 7.62 17.0635 8M11.5835 3C11.8535 3.1 12.3835 3.53 12.3235 4.36C12.2735 5.19 11.3935 5.56 11.3435 6.38C11.2835 7.16 11.6735 7.62 12.0635 8M6.5835 3C6.8535 3.1 7.3835 3.53 7.3335 4.36C7.2735 5.19 6.4035 5.56 6.3335 6.38C6.2835 7.16 6.6735 7.62 7.0735 8M12.3335 21C14.7204 21 17.0096 20.0518 18.6975 18.364C20.3853 16.6761 21.3335 14.3869 21.3335 12H3.3335C3.3335 14.3869 4.28171 16.6761 5.96953 18.364C7.65736 20.0518 9.94655 21 12.3335 21Z" stroke={sectionTabStroke('instructions')} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{tRecipes('drawer.instructions')}</span>
          </button>
          </div>
        </div>
      )}
    </div>
  );
}
